import { api, endpoints } from "api/api";
import { BaseApiPaging } from "api/types";
import { AxiosPromise } from "axios";

const create = (data: CreateScheduledEmailRequest): AxiosPromise<CreateScheduledEmailResponse> => {
  return api.post<CreateScheduledEmailResponse>(endpoints.SCHEDULED_EMAILS.CREATE(), data);
};

const update = (id: number, data: UpdateScheduledEmailRequest): AxiosPromise => {
  return api.put(endpoints.SCHEDULED_EMAILS.UPDATE(id), data);
};

const fetch = (
  skip: number,
  take: number,
  searchPhrase: string,
  filter: string = "",
  orderBy: string = "",
): AxiosPromise<BaseApiPaging<ScheduledEmailApi>> => {
  return api.get<BaseApiPaging<ScheduledEmailApi>>(
    endpoints.SCHEDULED_EMAILS.GET(skip, take, searchPhrase, filter, orderBy),
  );
};

const fetchById = (id: number): AxiosPromise<ScheduledEmailApi> => {
  return api.get<ScheduledEmailApi>(endpoints.SCHEDULED_EMAILS.GET_BY_ID(id));
};

const fetchRecipients = (id: number): AxiosPromise<ScheduledEmailRecipientApi[]> => {
  return api.get<ScheduledEmailRecipientApi[]>(endpoints.SCHEDULED_EMAILS.GET_RECIPIENTS(id));
};

const schedule = (id: number, deliveryTime: string): AxiosPromise => {
  return api.post(endpoints.SCHEDULED_EMAILS.SCHEDULE(id), { deliveryTime });
};

const sendImmediately = (id: number): AxiosPromise => {
  return api.post(endpoints.SCHEDULED_EMAILS.SEND(id));
};

const cancel = (id: number): AxiosPromise => {
  return api.post(endpoints.SCHEDULED_EMAILS.CANCEL(id));
};

const deleteEmail = (id: number): Promise<void> => {
  return api.delete(endpoints.SCHEDULED_EMAILS.DELETE(id));
};

export type CreateNewsletterSendRequest = {
  title: string;
  subject: string;
  htmlBody: string;
  jsonBody?: string;
};

const createNewsletter = (data: CreateNewsletterSendRequest): AxiosPromise<{ id: number }> => {
  return api.post<{ id: number }>(endpoints.SHOP.NEWSLETTER_SEND(), data);
};

export const scheduledEmailsApi = {
  create,
  createNewsletter,
  fetch,
  fetchById,
  fetchRecipients,
  update,
  schedule,
  sendImmediately,
  cancel,
  delete: deleteEmail,
};

export type CreateScheduledEmailRequest = {
  title: string;
  subject: string;
  htmlBody: string;
  jsonBody?: string;
  emailTemplateId?: number;
  recipients: RecipientRequest[];
};

export type UpdateScheduledEmailRequest = {
  title: string;
  subject: string;
  htmlBody: string;
  jsonBody?: string;
  emailTemplateId?: number;
  recipients: RecipientRequest[];
};

export type RecipientRequest = {
  userId: number | null;
  emailAddress: string;
  name?: string;
};

export type CreateScheduledEmailResponse = {
  id: number;
};

export type ScheduledEmailApi = {
  id: number;
  title: string;
  subject: string;
  htmlBody: string;
  jsonBody?: string;
  status: ScheduledEmailStatus;
  plannedDeliveryTime: string | null;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  createdDate: string;
  lastModified: string;
  isNewsletterSend?: boolean;
  createdByUser: {
    id: number;
    email: string;
  };
  emailTemplate?: {
    id: number;
    name: string;
  };
};

export type ScheduledEmailRecipientApi = {
  id: number;
  emailAddress: string;
  status: ScheduledEmailRecipientStatus;
  sentTime?: string;
  errorMessage?: string;
  user: {
    id: number;
    email: string;
    firstName: string;
    surname: string;
  } | null;
};

export enum ScheduledEmailStatus {
  Draft = 1,
  Scheduled = 2,
  Sending = 3,
  Sent = 4,
  Failed = 5,
  Cancelled = 6,
}

export enum ScheduledEmailRecipientStatus {
  Pending = 1,
  Sent = 2,
  Failed = 3,
}
